import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import {
  isDisabled$,
  TypedFormArray,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import { Organisation, Practice } from '@principle-theorem/principle-core';
import {
  type IPractice,
  type IRole,
  type IUser,
} from '@principle-theorem/principle-core/interfaces';
import {
  asReffable,
  asyncForEach,
  type CollectionReference,
  type DocumentReference,
  filterUndefined,
  Firestore,
  isSameRef,
  snapshot,
  where,
  type WithRef,
} from '@principle-theorem/shared';
import { compact, first, uniq, uniqWith } from 'lodash';
import { BehaviorSubject, combineLatest, type Observable } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';

export type InviteFormData = Pick<
  IUser,
  'name' | 'email' | 'roles' | 'practices' | 'brands'
>;

interface IInviteUserFormData {
  name: string;
  email: string;
  roles: DocumentReference<IRole>[];
  practices: DocumentReference<IPractice>[];
}

interface IInviteFormData {
  emails: string;
  users: IInviteUserFormData[];
}

export interface IInviteUserDialogData {
  usersCollection: CollectionReference<IUser>;
}

function getUserFormGroup(): TypedFormGroup<IInviteUserFormData> {
  return new TypedFormGroup<IInviteUserFormData>({
    name: new TypedFormControl<string>('', [Validators.required]),
    email: new TypedFormControl<string>('', [
      Validators.email,
      Validators.required,
    ]),
    roles: new TypedFormControl<DocumentReference<IRole>[]>([]),
    practices: new TypedFormControl<DocumentReference<IPractice>[]>([]),
  });
}

@Component({
  selector: 'pr-invite-user-dialog',
  templateUrl: './invite-user-dialog.component.html',
  styleUrls: ['./invite-user-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class InviteUserDialogComponent {
  private _data = inject<IInviteUserDialogData>(MAT_DIALOG_DATA);
  private _dialog = inject(
    MatDialogRef<InviteUserDialogComponent, InviteFormData[]>
  );
  private _snackBar = inject(MatSnackBar);
  private _organisation = inject(OrganisationService);

  inviteForm = new TypedFormGroup<IInviteFormData>({
    emails: new TypedFormControl(),
    users: new TypedFormArray([]),
  });
  roles$: Observable<WithRef<IRole>[]>;
  practices$: Observable<WithRef<IPractice>[]>;
  hasMultiplePractices$: Observable<boolean>;
  isDisabled$: Observable<boolean>;
  loading$ = new BehaviorSubject<boolean>(false);

  get usersArray(): TypedFormArray<IInviteUserFormData> {
    return this.inviteForm.controls
      .users as TypedFormArray<IInviteUserFormData>;
  }

  constructor() {
    this.roles$ = this._organisation.organisation$.pipe(
      filterUndefined(),
      switchMap((organisation) => Organisation.roles$(organisation))
    );

    this.practices$ = this._organisation.allUserPractices$;
    this.hasMultiplePractices$ = this._organisation.hasMultiplePractices$;

    this.isDisabled$ = combineLatest([
      isDisabled$(this.inviteForm),
      this.inviteForm.valueChanges,
    ]).pipe(
      map(([isDisabled, value]) => {
        if (!value.users || !value.users.length) {
          return true;
        }
        return isDisabled;
      }),
      startWith(true)
    );
  }

  async addEmails(): Promise<void> {
    const emails = compact(
      (this.inviteForm.controls.emails.value ?? '')
        .toLowerCase()
        .split(new RegExp(/,|\s/))
    );

    const duplicateEmails: string[] = [];
    const validEmails: string[] = [];

    await asyncForEach(emails, async (email) => {
      const cleanedEmail = email.trim();
      const existingUser = await Firestore.firstResult(
        this._data.usersCollection,
        where('email', '==', cleanedEmail)
      );
      if (existingUser) {
        duplicateEmails.push(cleanedEmail);
        return;
      }

      validEmails.push(cleanedEmail);
    });

    if (duplicateEmails.length) {
      this._snackBar.open(
        `A user already exists for the following email addresses: ${duplicateEmails.join(
          ', '
        )}`
      );
    }

    const practice = await snapshot(
      combineLatest([this.practices$, this.hasMultiplePractices$]).pipe(
        map(([practices, hasMultiple]) => {
          if (hasMultiple) {
            return;
          }
          return first(practices.map(({ ref }) => ref));
        })
      )
    );

    uniq(validEmails).map((email) => {
      const formGroup = getUserFormGroup();
      formGroup.patchValue({ email, practices: compact([practice]) });
      this.usersArray.push(formGroup);
    });

    this.inviteForm.controls.emails.reset('');
  }

  removeEmail(index: number): void {
    this.usersArray.removeAt(index);
  }

  submit(): void {
    this.loading$.next(true);
    const formData = this.inviteForm.getRawValue();
    const users: InviteFormData[] = formData.users.map((user) => {
      const brands = uniqWith(
        user.practices.map((practice) =>
          Practice.brandRef(asReffable(practice))
        ),
        isSameRef
      );

      return {
        name: user.name.trim(),
        email: user.email.trim().toLowerCase(),
        roles: user.roles,
        practices: user.practices,
        brands,
      };
    });
    this._dialog.close(users);
    this.loading$.next(false);
  }
}
